import { Typography, Button } from '@mui/material'
import React, { useEffect, useState } from 'react'
import Stack from '@mui/material/Stack'
import {
  breakdownDataService,
  getLastUpdatedDate,
  getLatestData
} from '../../service/uptime.service.js'
import { showNotification } from '../../utils/toast.utils'

import StyledTable from '../../components/ReactTable/StyledTable.js'
import StyledEmptyHeader from '../../components/StyledEmptyHeader/StyledEmptyHeader.js'
import StyledSelectField from '../../components/StyledSelectField/StyledSelectField.js'
import StyledTableContainer from '../../components/StyledTableContainer/StyledTableContainer.js'
import { useDashboardGlobalStateContext } from '../../context/DashboardGlobalStateContext.js'

const defaultMachine = {
  value: '',
  label: 'Select Machine'
}
const defaultFilterOptionMttr = {
  value: 'All MTTR',
  label: 'All'
}
const defaultFilterOptionMtbf = {
  value: 'All MTBF',
  label: 'All'
}

function formatMinutes(minutes) {
  if (minutes >= 1440) {
    const days = Math.floor(minutes / 1440)
    const remainingMinutes = minutes % 1440
    const hours = Math.floor(remainingMinutes / 60)
    const finalMinutes = (remainingMinutes % 60).toFixed(0)
    return `${days} days ${hours} hrs ${finalMinutes} mins`
  } else if (minutes >= 60) {
    const hours = Math.floor(minutes / 60)
    const finalMinutes = (minutes % 60).toFixed(0)
    return `${hours} hrs ${finalMinutes} mins`
  }
  return `${parseFloat(minutes).toFixed(0)} mins`
}

const calculateAverage = (arr, key) => {
  if (arr.length === 0) return 0

  const sum = arr.reduce((acc, obj) => acc + obj[key], 0)
  return sum / arr.length
}

const Uptime = () => {
  const [machine, setMachine] = useState(defaultMachine)
  const [filterData, setfilterData] = useState(defaultFilterOptionMttr)
  const [filterDataMtbf, setfilterDataMtbf] = useState(defaultFilterOptionMtbf)

  const [MttrFilterOptions, setMttrFilterOptions] = useState([])
  const [fetchingData, setFetchingData] = useState(false)
  const [processingBreakDown, setProcessingBreakDown] = useState(false)
  const [latestDate, setLatestDate] = useState(null)
  const [breakdownDataRows, setBreakdownDataRows] = useState([])
  const [filteredBreakdownDataRows, setFilteredBreakdownDataRows] = useState([])
  const [totalRows, setTotalRows] = useState(0)
  const [pageNo, setPageNo] = useState(1)
  const pageSize = 11

  const totalPages = Math.ceil(totalRows / pageSize)

  const { machines: machineOptions } = useDashboardGlobalStateContext()

  const fetchMttrMtbfFilterOptions = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_ADMIN_BASE_URL}/v1/filter-config`
      )
      const data = await response.json()

      if (data?.data) {
        const options = data.data.map((item) => {
          const filterValues = item.filterData.map((value) => ({
            value: value,
            label: `${value} mins`
          }))
          filterValues.unshift({ value: 'All', label: `All ${item.parameter}` })
          return {
            value: item.parameter,
            label: item.parameter,
            filterValues: filterValues
          }
        })
        setMttrFilterOptions(options)
        if (options.length > 0) {
          setfilterData(options[0].filterValues[0])
          setfilterDataMtbf(
            options.find((opt) => opt.value === 'MTBF')?.filterValues[0] || {
              value: 'All',
              label: 'All MTBF'
            }
          )
        }
      }
    } catch (error) {
      console.error('Error fetching filter options:', error)
      showNotification('Failed to fetch filter options', 'error')
    }
  }

  useEffect(() => {
    fetchMttrMtbfFilterOptions()
  }, [])

  useEffect(() => {
    if (machineOptions.length > 0) {
      setMachine(machineOptions[0])
    }
  }, [machineOptions])

  useEffect(() => {
    const fetchData = async () => {
      if (!machine.value) return

      try {
        setFetchingData(true)

        const [breakdownResponse, latestDateResponse] = await Promise.all([
          breakdownDataService({ machine: machine.value, pageNo, pageSize }),
          getLastUpdatedDate({ machine: machine.value, unit: 4 })
        ])

        if (latestDateResponse.data?.success && latestDateResponse.data?.data) {
          setLatestDate(latestDateResponse.data.data.lastProcessedDate || 'N/A')
        }

        if (
          breakdownResponse.data?.status === 'success' &&
          Array.isArray(breakdownResponse.data.data.rows)
        ) {
          setBreakdownDataRows(breakdownResponse.data.data.rows)
          setTotalRows(breakdownResponse.data.data.totalRows)
        } else {
          showNotification('No breakdown data found', 'warning')
        }
      } catch (err) {
        console.error('Error fetching breakdown data:', err)
        showNotification('Failed to fetch breakdown data', 'error')
      } finally {
        setFetchingData(false)
      }
    }

    fetchData()
  }, [machine, pageNo])

  useEffect(() => {
    setPageNo(1)
  }, [machine])

  useEffect(() => {
    let filteredData = breakdownDataRows

    if (filterData && filterData.value !== 'All') {
      filteredData = filteredData.filter((row) => row.ttr >= filterData.value)
    }

    if (filterDataMtbf && filterDataMtbf.value !== 'All') {
      filteredData = filteredData.filter(
        (row) => row.tbf >= filterDataMtbf.value
      )
    }

    setFilteredBreakdownDataRows(filteredData)
  }, [filterData, filterDataMtbf, breakdownDataRows])

  const getLatestDataByDate = async () => {
    if (!machine.value) return

    try {
      setProcessingBreakDown(true)
      const response = await getLatestData({ machine: machine.value, unit: 4 })

      if (response.data?.success && response.data?.data) {
        setBreakdownDataRows(response.data.data.rows)
        setTotalRows(response.data.data.totalRows)
      }
    } catch (error) {
      console.error('Error fetching latest data:', error)
      showNotification(`Failed to fetch latest data: ${error.message}`, 'error')
    } finally {
      setProcessingBreakDown(false)
    }
  }

  const MTTR = formatMinutes(calculateAverage(filteredBreakdownDataRows, 'ttr'))
  const MTBF = formatMinutes(calculateAverage(filteredBreakdownDataRows, 'tbf'))

  const columns = [
    {
      Header: 'Start time',
      accessor: '',
      Cell: ({ cell: { row } }) => (
        <>{`${row.original.startDate} ${row.original.startTime}`}</>
      )
    },
    {
      Header: 'End time',
      accessor: '',
      Cell: ({ cell: { row } }) => (
        <>{`${row.original.endDate} ${row.original.endTime}`}</>
      )
    },
    { Header: 'Count', accessor: 'count' },
    {
      Header: 'TTR',
      accessor: 'ttr',
      Cell: ({ cell: { row } }) => formatMinutes(row.original.ttr)
    },
    {
      Header: 'TBF',
      accessor: 'tbf',
      Cell: ({ cell: { row } }) => formatMinutes(row.original.tbf)
    }
  ]

  return (
    <>
      <Stack
        sx={{
          position: 'sticky',
          top: 0,
          width: '100%',
          flexDirection: 'column',
          justifyContent: 'space-between',
          gap: '8px',
          padding: '16px',
          backgroundColor: '#fff',
          borderBottom: '1px solid #eeeeee'
        }}
      >
        <Stack
          sx={{
            flexDirection: 'row',
            gap: '16px'
          }}
        >
          <StyledSelectField
            label=""
            placeHolderText="Select MTTR"
            selectedValue={filterData}
            selectOptions={
              MttrFilterOptions.length > 0
                ? MttrFilterOptions.find((opt) => opt.value === 'MTTR')
                    ?.filterValues || []
                : []
            }
            onChangeHandler={(value) => setfilterData(value)}
            isMulti={false}
            isSearchable={true}
            noOptionsMessage="No filter data found"
            sx={{ minWidth: '100px', height: '40px' }}
          />

          <StyledSelectField
            label=""
            placeHolderText="Select MTBF"
            selectedValue={filterDataMtbf}
            selectOptions={
              MttrFilterOptions.length > 0
                ? MttrFilterOptions.find((opt) => opt.value === 'MTBF')
                    ?.filterValues || []
                : []
            }
            onChangeHandler={(value) => setfilterDataMtbf(value)}
            isMulti={false}
            isSearchable={true}
            noOptionsMessage="No filter data found"
            sx={{ minWidth: '100px', height: '40px' }}
          />

          <StyledSelectField
            label=""
            placeHolderText="Select Machine"
            selectedValue={machine}
            selectOptions={machineOptions}
            onChangeHandler={(value) => setMachine(value)}
            isMulti={false}
            isSearchable={true}
            isDisabled={processingBreakDown || fetchingData}
            noOptionsMessage="No machine found"
            sx={{ minWidth: '100px', height: '40px' }}
          />

          <Button
            disabled={!machine.value || processingBreakDown}
            onClick={getLatestDataByDate}
            variant="contained"
            sx={{
              position: 'relative',
              width: '600px',
              height: '50px',
              backgroundColor: '#fff',
              boxShadow: '0 2px 7px 0 rgb(18 18 19 / 6%)',
              transition:
                'box-shadow 300ms ease, color 300ms ease, border-color 300ms ease',
              color: '#000',
              borderRadius: '8px',
              border: '1px solid #d9d9d9',
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: '16px',
              '&:hover': {
                borderColor: '#a8a8b5',
                boxShadow: '0 2px 12px 0 rgb(18 18 19 / 10%)',
                backgroundColor: '#fafafa'
              }
            }}
          >
            {processingBreakDown ? 'Processing...' : 'Get latest data'}
          </Button>
        </Stack>
        <Stack sx={{ marginRight: 'auto', flexDirection: 'row', gap: '16px' }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>
            Last processed date: {latestDate ? latestDate : 'N/A'}
          </Typography>
          <Typography sx={{ fontWeight: 400 }}>
            MTTR: <span style={{ fontWeight: 600 }}>{MTTR}</span>
          </Typography>
          <Typography sx={{ fontWeight: 400 }}>
            MTBF: <span style={{ fontWeight: 600 }}>{MTBF}</span>
          </Typography>
        </Stack>
      </Stack>

      <Stack sx={{ gap: '16px', padding: '16px' }}>
        <StyledTableContainer>
          {processingBreakDown || fetchingData ? (
            <StyledEmptyHeader>Loading...</StyledEmptyHeader>
          ) : filteredBreakdownDataRows.length === 0 ? (
            <StyledEmptyHeader>
              No uptime data available, please contact support.
            </StyledEmptyHeader>
          ) : (
            <StyledTable
              pagination={false}
              columns={columns}
              data={filteredBreakdownDataRows}
              hiddenColumns={['count']}
            />
          )}
        </StyledTableContainer>
        <Stack
          direction="row"
          spacing={2}
          justifyContent="space-between"
          sx={{ marginTop: '16px' }}
        >
          <Typography variant="body1">
            Page {pageNo} of {totalPages}
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              size="small"
              disabled={pageNo === 1}
              onClick={() => setPageNo((prev) => Math.max(prev - 1, 1))}
            >
              Prev
            </Button>
            <Button
              variant="outlined"
              size="small"
              disabled={pageNo === totalPages}
              onClick={() => setPageNo((prev) => prev + 1)}
            >
              Next
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </>
  )
}

export default Uptime
